import { SupabaseProfileUtils } from "@/lib/db/utils/supabase/profiles";
import { supabase } from "@/lib/supabase";
import { fileToBase64 } from "@/lib/utils/fileUtils";
import {
  clearOAuthState,
  detectIncognitoMode,
  getRedirectUrl,
  getBaseDomain,
  logOAuthConfig,
} from "@/utils/auth";
import { create } from "zustand";
import { persist } from "zustand/middleware";

// Helper function to find the earliest created workspace
const findEarliestWorkspace = (workspaces) => {
  if (!workspaces || workspaces.length === 0) return null;

  // Sort by created_at date (earliest first) and return the first one
  return (
    workspaces
      .filter((w) => w && w.created_at) // Filter out any invalid workspaces
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0] ||
    workspaces[0]
  );
};

const STORAGE_KEY = "auth-storage";
const VALID_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

// Function to clear all Zustand stores from localStorage
const clearAllStores = (get, set) => {
  // Before clearing, save the current workspace information
  const currentWorkspaceData = {
    workspace: get().currentWorkspace,
    workspaceId: get().currentWorkspaceId,
    workspaces: get().workspaces,
  };

  // Clear the store
  set({
    user: null,
    profile: null,
    profileData: {}, // Explicitly clear profileData
    isLoggedIn: false,
    isLoading: false,
    isOnboardingComplete: false,
    hasCheckedSession: true,
    currentWorkspace: null,
    currentWorkspaceId: null,
    workspaces: [],
    assistantsInitialized: false, // New flag to track if assistants were already initialized
    // Other reset states...
  });

  // Import and reset other stores here
  const resetOtherStores = async () => {
    try {
      // Dynamically import other stores to avoid circular dependencies
      const assistantStore = (await import("./assistantStore"))
        .useAssistantStore;
      const threadStore = (await import("./threadStore")).useThreadStore;
      const notificationStore = (await import("./notificationStore"))
        .useNotificationStore;

      // Reset other stores
      if (assistantStore && typeof assistantStore.getState === "function") {
        assistantStore.getState().resetStore();
      }
      if (threadStore && typeof threadStore.getState === "function") {
        threadStore.getState().resetStore();
      }
      if (
        notificationStore &&
        typeof notificationStore.getState === "function"
      ) {
        notificationStore.getState().resetStore();
      }
    } catch (error) {
      console.error("Error resetting other stores:", error);
    }
  };

  resetOtherStores();

  return currentWorkspaceData; // Return the workspace data in case we need to restore it
};

// Function to determine the best storage method based on incognito status
const getStorage = () => {
  // Default to localStorage, but use sessionStorage as fallback
  let storage = typeof window !== "undefined" ? window.localStorage : null;

  // If localStorage fails, switch to sessionStorage
  try {
    const testKey = "storage_test";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
  } catch (e) {
    storage = typeof window !== "undefined" ? window.sessionStorage : null;
  }

  return storage;
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // General state
      isLoading: false,
      error: null,
      initialized: false,
      isIncognitoMode: false,
      isHandlingSessionExpiry: false,

      // User state
      user: null,
      session: null,
      onboardingComplete: false,
      userSettings: {},
      userId: null,

      // Profile state
      profile: null,
      profileData: {},

      // Workspace state
      workspaces: [],
      currentWorkspace: null,
      currentWorkspaceId: null,
      currentWorkspaceSettings: {},

      // Additional state
      filePathRoot: "",
      storedAvatars: [],
      selectedPreviewUrl: null,

      isProfileComplete: false,

      // Make isAuthenticated a robust function that will always be a function
      isAuthenticated: function () {
        // Inside the function, use safe references to avoid errors
        try {
          return !!(get() && get().user);
        } catch (e) {
          console.error("Error in isAuthenticated:", e);
          return false;
        }
      },

      /* ---------------------------- */
      /* --------- SETTERS ---------- */
      /* ---------------------------- */
      setSelectedPreviewUrl: (url) => set({ selectedPreviewUrl: url }),

      setUser: (user) => {
        // More robust user setting that ensures userId is always set
        // console.log("Setting user in auth store:", user?.id);
        set({
          user,
          userId: user?.id,
          // Ensure partial initialization state is captured
          isAuthenticated: !!user,
        });
      },

      setSession: (session) => {
        // When setting session, also set user data if available
        if (session?.user) {
          set({
            session,
            user: session.user,
            userId: session.user.id,
            isAuthenticated: true,
          });
        } else {
          set({ session });
        }
      },

      setOnboardingComplete: (value) => set({ onboardingComplete: value }),

      // Add method to set profile directly
      setProfile: (profile) => {
        if (!profile) {
          console.warn("setProfile called with null/undefined profile");
          return;
        }

        try {
          // Only use the top-level onboarding_completed field
          const isOnboardingComplete = !!profile.onboarding_completed;

          // Ensure profileData is properly set with default structure if settings is missing
          const profileData = profile.settings || {
            profile: {},
            account: {},
            personalization: {},
            memory: {},
            dataControls: {},
            subscription: {},
            beta: {},
            system: {
              theme: "system",
              language: "en",
              autoSuggest: false,
              aiModel: "default",
              imageModel: "playground-v2",
            },
          };

          console.log("🔄 [AuthStore] setProfile called with:", {
            profileId: profile.id,
            avatarUrl: profile.avatar_url
              ? profile.avatar_url.substring(0, 50) + "..."
              : null,
            fullName: profile.full_name,
            onboardingCompleted: profile.onboarding_completed,
            settingsAvatarUrl: profile.settings?.profile?.avatar_url
              ? profile.settings.profile.avatar_url.substring(0, 50) + "..."
              : null,
          });

          // Set state with the correct onboarding status and ensure profileData is always set
          set({
            profile,
            profileData,
            isProfileComplete: !!(profile.full_name && profile.avatar_url),
            onboardingComplete: isOnboardingComplete,
          });

          console.log("✅ [AuthStore] Profile state updated successfully");

          // If onboarding_completed isn't set but should be, update it
          if (
            !profile.onboarding_completed &&
            profile.settings?.profile?.onboarding_completed
          ) {
            // Fix inconsistent state by updating the database profile
            (async () => {
              try {
                const { error, data } = await supabase
                  .from("profiles")
                  .update({ onboarding_completed: true })
                  .eq("id", profile.id)
                  .select()
                  .single();

                if (error) {
                  console.error(
                    "Error updating onboarding_completed field:",
                    error
                  );
                } else {
                  // Update local state with the verified value
                  set((state) => ({
                    profile: {
                      ...state.profile,
                      onboarding_completed: true,
                    },
                    onboardingComplete: true,
                  }));
                }
              } catch (updateError) {
                console.error(
                  "Error in onboarding_completed update:",
                  updateError
                );
              }
            })();
          }
        } catch (error) {
          console.error("Error in setProfile method:", error);
          // Still try to set basic profile data even if there's an error
          set({
            profile,
            profileData: profile.settings || {},
            isProfileComplete: !!(profile.full_name && profile.avatar_url),
            onboardingComplete: !!profile.onboarding_completed,
          });
        }
      },

      // Add method to set workspaces array
      setWorkspaces: (workspaces) => {
        if (Array.isArray(workspaces) && workspaces.length > 0) {
          set({ workspaces });
          // If no current workspace is set but we have workspaces, set the first one
          if (!get().currentWorkspaceId && workspaces.length > 0) {
            const personalWorkspace =
              workspaces.find((w) => w.is_personal) || workspaces[0];
            set({
              currentWorkspace: personalWorkspace,
              currentWorkspaceId: personalWorkspace.id,
            });
          }
        }
      },

      // Add method to set current workspace ID
      setCurrentWorkspaceId: (workspaceId) => {
        if (!workspaceId) return;

        set({ currentWorkspaceId: workspaceId });

        // Update filePathRoot if user ID is available
        const userId = get().userId;
        if (userId) {
          set({ filePathRoot: `${userId}/${workspaceId}` });
        }
      },

      /* ---------------------------- */
      /* --- UNIFIED INITIALIZATION --- */
      /* ---------------------------- */

      /**
       * Single unified initialization function that loads all necessary auth data
       * in a sequential, predictable flow. This replaces the scattered initialization
       * functions to provide a more reliable and maintainable approach.
       */
      initialize: async () => {
        const state = get();

        // Prevent multiple initialization calls
        if (state.initialized) {
          return {
            success: true,
            user: state.user,
            profile: state.profile,
            workspaces: state.workspaces,
            reason: "Already initialized",
          };
        }

        // Check if we have a cached result from a recent initialization
        const cachedResult = get().cachedInitResult;
        if (cachedResult && Date.now() - cachedResult.timestamp < 5000) {
          return cachedResult.result;
        }

        // Check if initialization is already in progress
        if (get().isLoading) {
          return {
            success: false,
            reason: "Initialization in progress",
          };
        }

        set({ isLoading: true, error: null });

        try {
          const startTime = Date.now();

          // Clear any stale state
          set({
            user: null,
            profile: null,
            workspaces: [],
            currentWorkspace: null,
            currentWorkspaceId: null,
          });

          // Step 1: Get current session
          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession();

          if (sessionError) {
            throw new Error(`Session error: ${sessionError.message}`);
          }

          if (!session?.user) {
            set({
              initialized: true,
              isLoading: false,
              user: null,
              profile: null,
              workspaces: [],
            });

            const result = {
              success: false,
              reason: "No active session",
              user: null,
              profile: null,
              workspaces: [],
            };

            // Cache the result
            set({
              cachedInitResult: {
                result,
                timestamp: Date.now(),
              },
            });

            return result;
          }

          // Step 1.5: Validate session with server (getUser makes an API call)
          // This catches cases where the session was invalidated server-side
          const { data: userData, error: userError } = await supabase.auth.getUser();

          if (userError || !userData?.user) {
            console.warn('[Auth] Session invalid on server, clearing local session');
            // Clear invalid session
            await supabase.auth.signOut({ scope: 'local' });
            localStorage.removeItem('app-session');

            set({
              initialized: true,
              isLoading: false,
              user: null,
              session: null,
              profile: null,
              workspaces: [],
            });

            // Redirect to login if not already on auth page
            if (!window.location.pathname.startsWith('/auth')) {
              window.location.href = '/auth/login?reason=session_expired';
            }

            return {
              success: false,
              reason: "Session expired",
              user: null,
              profile: null,
              workspaces: [],
            };
          }

          // Step 2: Set session and user data
          set({
            session,
            user: session.user,
            userId: session.user.id,
            isAuthenticated: true,
          });

          // Step 3: Load or create profile

          let profile = null;
          const { data: existingProfile, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", session.user.id)
            .single();

          if (profileError && profileError.code !== "PGRST116") {
            throw new Error(`Profile fetch error: ${profileError.message}`);
          }

          if (!existingProfile) {
            const newProfile = {
              id: session.user.id,
              email: session.user.email,
              full_name: session.user.user_metadata?.full_name || "",
              avatar_url: session.user.user_metadata?.avatar_url || "",
              onboarding_completed: false,
              settings: {
                profile: {
                  display_name: "",
                  email: "",
                  bio: "",
                  avatar_url: "",
                  profile_context: "",
                },
                account: {
                  security: {
                    twoFactorEnabled: false,
                    emailNotifications: false,
                    pushNotifications: false,
                  },
                  subscription: {
                    plan: "free",
                    billingCycle: "monthly",
                    paymentMethod: {
                      type: "card",
                      last4: "",
                    },
                  },
                },
                security: {
                  multiFactorEnabled: false,
                  sessionManagement: {
                    logOutAllDevices: false,
                  },
                },
                personalization: {
                  name: "",
                  role: "",
                  traits: [],
                  enable_profile_memory: true, // Default ON for memory in system prompt
                  enable_profile_context: true,
                },
                dataControls: {
                  dataExport: {
                    lastExportDate: null,
                    exportFormat: "json",
                  },
                  analytics: false,
                  usageTracking: false,
                  dataCollection: {
                    analyticsEnabled: false,
                    usageTrackingEnabled: false,
                  },
                },
                memory: {
                  contextualMemory: false,
                  longTermMemory: false,
                  conversationHistory: {
                    enabled: true,
                    retentionPeriod: 30,
                  },
                },
                beta: {
                  advancedAiModels: false,
                  voiceChat: false,
                  customPlugins: false,
                  betaProgramAccess: false,
                  apiKeys: {
                    anthropic: null,
                    openai: null,
                    ollama: null,
                    groq: null,
                    google: null,
                    mistral: null,
                    xai: null,
                    openrouter: null,
                    cohere: null,
                    fireworks: null,
                    fal: null,
                    deepseek: null,
                    qwen: null,
                    perplexity: null,
                    github: null,
                    pinecone: null,
                    togetherai: null,
                  },
                },
                system: {
                  textSize: "md",
                  autoSuggest: false,
                },
                models: {
                  "gpt-4.1": true,
                  "gpt-4.1-mini": false,
                  "gpt-4.1-nano": false,
                  "gpt-4o": false,
                  "gpt-4o-mini": false,
                  "gpt-5": true,
                  "gpt-5-mini": true,
                  "gpt-5-nano": false,
                  "gpt-5.1": true,
                  "gpt-5.1-codex-mini": false,
                  "gpt-5.1-codex": false,
                  "claude-opus-4-20250514": true,
                  "claude-sonnet-4-20250514": true,
                  "claude-3-7-sonnet-20250219": true,
                  "claude-3-5-haiku-20241022": true,
                  "gemini-2.5-pro": true,
                  "gemini-2.5-flash": true,
                  "gemini-2.5-flash-lite": true,
                  "gemini-3-pro-preview": true,
                  "mistral-small-latest": false,
                  "mistral-large-latest": false,
                  "open-mistral-nemo": false,
                  "deepseek-chat": false,
                  "deepseek-reasoner": false,
                  "llama-3.3-70b-versatile": false,
                  "grok-4-0709": false,
                  "grok-code-fast-1": false,
                  "grok-3": false,
                  "grok-3-fast": false,
                  "grok-3-mini": false,
                  "grok-3-mini-fast": false,
                  "command-a-03-2025": false,
                  "command-r7b-12-2024": false,
                  "command-r-plus-08-2024": false,
                  "command-r-08-2024": false,
                  sonar: false,
                  "sonar-pro": false,
                  "sonar-deep-research": false,
                  "liquid/lfm-7b": false,
                },
              },
              user_workspaces: [],
            };

            const { data: createdProfile, error: createError } = await supabase
              .from("profiles")
              .insert([newProfile])
              .select()
              .single();

            if (createError) {
              throw new Error(`Profile creation error: ${createError.message}`);
            }

            profile = createdProfile;
          } else {
            profile = existingProfile;
          }

          // Step 4: Load workspaces for user

          // First try to get workspaces where user is a member
          const { data: memberWorkspaces, error: memberError } = await supabase
            .from("workspaces")
            .select("*")
            .contains("workspace_members", [session.user.id]);

          if (memberError) {
            console.error("Error fetching member workspaces:", memberError);
          }

          // Also get workspaces where user is the owner
          const { data: ownedWorkspaces, error: ownerError } = await supabase
            .from("workspaces")
            .select("*")
            .eq("owner_id", session.user.id);

          if (ownerError) {
            console.error("Error fetching owned workspaces:", ownerError);
          }

          // Combine and deduplicate workspaces
          const allWorkspaces = [];
          const workspaceIds = new Set();

          // Add member workspaces
          if (memberWorkspaces) {
            memberWorkspaces.forEach((workspace) => {
              if (!workspaceIds.has(workspace.id)) {
                allWorkspaces.push(workspace);
                workspaceIds.add(workspace.id);
              }
            });
          }

          // Add owned workspaces
          if (ownedWorkspaces) {
            ownedWorkspaces.forEach((workspace) => {
              if (!workspaceIds.has(workspace.id)) {
                allWorkspaces.push(workspace);
                workspaceIds.add(workspace.id);
              }
            });
          }

          // Step 5: Handle workspace creation if none exist
          let workspaces = allWorkspaces;
          let currentWorkspaceId = null;

          if (workspaces.length === 0) {
            const personalWorkspace = {
              user_id: session.user.id,
              owner_id: session.user.id,
              name: "Personal Project",
              is_personal: true, // Ensure this is explicitly set to true
              settings: {},
              workspace_members: [session.user.id],
              metadata: {
                created_at: new Date().toISOString(),
                created_by: session.user.id,
              },
            };

            const { data: newWorkspace, error: createWorkspaceError } =
              await supabase
                .from("workspaces")
                .insert([personalWorkspace])
                .select()
                .single();

            if (createWorkspaceError) {
              console.error(
                "Error creating personal workspace:",
                createWorkspaceError
              );
              // Continue without workspace - not a fatal error
            } else {
              workspaces = [newWorkspace];
              currentWorkspaceId = newWorkspace.id;

              // Update profile with the new workspace
              try {
                await supabase
                  .from("profiles")
                  .update({
                    user_workspaces: [newWorkspace.id],
                  })
                  .eq("id", session.user.id);
              } catch (updateError) {
                console.error(
                  "Error updating profile with workspace:",
                  updateError
                );
              }
            }
          } else {
            // Set current workspace to personal workspace or first available
            const personalWorkspace = workspaces.find((w) => w.is_personal);
            currentWorkspaceId = personalWorkspace
              ? personalWorkspace.id
              : workspaces[0].id;
          }

          // Step 6: Initialize avatar data
          const storedAvatars = await get().fetchStoredAvatars("avatars");

          // Set final state
          set({
            profile,
            profileData: profile.settings || {},
            workspaces,
            currentWorkspaceId,
            currentWorkspace:
              workspaces.find((w) => w.id === currentWorkspaceId) || null,
            currentWorkspaceSettings:
              workspaces.find((w) => w.id === currentWorkspaceId)?.settings ||
              {},
            storedAvatars,
            onboardingComplete: !!profile.onboarding_completed,
            isProfileComplete: !!(profile.full_name && profile.avatar_url),
            initialized: true,
            isLoading: false,
          });

          const result = {
            success: true,
            user: session.user,
            profile,
            workspaces,
            currentWorkspaceId,
          };

          // Cache the result for 5 seconds to prevent redundant calls
          set({
            cachedInitResult: {
              result,
              timestamp: Date.now(),
            },
          });

          return result;
        } catch (error) {
          console.error("Auth initialization failed:", error);
          set({
            initialized: false,
            isLoading: false,
            error: error.message,
          });

          const result = {
            success: false,
            error: error.message,
            user: null,
            profile: null,
            workspaces: [],
          };

          // Cache the error result briefly
          set({
            cachedInitResult: {
              result,
              timestamp: Date.now(),
            },
          });

          return result;
        }
      },

      // Legacy method - now just calls the unified initialize method
      initializeAuth: async () => {
        return await get().initialize();
      },

      initializeUserData: async (user) => {
        // If we already have the data initialized and it matches the user, just return it
        if (get().initialized && get().user?.id === user?.id) {
          return {
            profile: get().profile,
            workspaces: get().workspaces,
            currentWorkspace: get().currentWorkspace,
            currentWorkspaceId: get().currentWorkspaceId,
          };
        }

        // Otherwise re-initialize
        const result = await get().initialize();
        if (result.success) {
          return {
            profile: get().profile,
            workspaces: get().workspaces,
            currentWorkspace: get().currentWorkspace,
            currentWorkspaceId: get().currentWorkspaceId,
          };
        }

        throw new Error("Failed to initialize user data");
      },

      /* ------------------------ */
      /* ----- Auth Methods ----- */
      /* ------------------------ */
      loginWithEmail: async (email) => {
        set({ loading: true, error: null });

        try {
          // Get the correct redirect URL
          const redirectUrl = getRedirectUrl();

          const { data, error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              emailRedirectTo: redirectUrl,
              shouldCreateUser: true, // Allow automatic user creation for login
            },
          });

          if (error) {
            console.error("Error sending magic link:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          // For magic link, we don't set user/session here since they need to click the link first
          // The actual authentication happens in the auth callback
          return { success: true, data, magicLinkSent: true };
        } catch (error) {
          console.error("Error sending magic link:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // New method for email/password authentication (for dev login)
      loginWithEmailPassword: async (email, password) => {
        set({ loading: true, error: null });

        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) {
            console.error("Error signing in with email/password:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          if (data?.user && data?.session) {
            // Set user and session
            set({
              user: data.user,
              session: data.session,
              userId: data.user.id,
            });

            return {
              success: true,
              data,
              user: data.user,
              session: data.session,
            };
          }

          return {
            success: false,
            error: new Error("No user or session in login response"),
          };
        } catch (error) {
          console.error("Error signing in with email/password:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // New method for handling magic link verification (used in auth callback)
      verifyMagicLink: async (tokenHash, type = "email") => {
        set({ loading: true, error: null });

        try {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });

          if (error) {
            console.error("Error verifying magic link:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          if (data?.user && data?.session) {
            // Set user and session
            set({
              user: data.user,
              session: data.session,
              userId: data.user.id,
            });

            return {
              success: true,
              data,
              user: data.user,
              session: data.session,
            };
          }

          return {
            success: false,
            error: new Error("No user or session in verification response"),
          };
        } catch (error) {
          console.error("Error verifying magic link:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // New method for OTP verification (if using OTP instead of magic link)
      verifyEmailOtp: async (email, token) => {
        set({ loading: true, error: null });

        try {
          const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type: "email",
          });

          if (error) {
            console.error("Error verifying OTP:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          if (data?.user && data?.session) {
            // Set user and session
            set({
              user: data.user,
              session: data.session,
              userId: data.user.id,
            });

            return {
              success: true,
              data,
              user: data.user,
              session: data.session,
            };
          }

          return {
            success: false,
            error: new Error("No user or session in OTP verification response"),
          };
        } catch (error) {
          console.error("Error verifying OTP:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Improved GitHub login implementation
      loginWithGitHub: async () => {
        set({ loading: true, error: null });

        try {
          // Always clear any existing OAuth state before starting a new flow
          // This ensures we don't have stale PKCE verifiers or flow state
          await clearOAuthState();

          // Small delay to ensure cleanup is complete
          await new Promise((resolve) => setTimeout(resolve, 200));

          // Check for incognito mode
          const isIncognitoMode = await detectIncognitoMode();
          set({ isIncognitoMode });

          // Get the correct redirect URL
          const githubRedirectUrl = getRedirectUrl();

          // Log for debugging
          console.log(
            "Initiating GitHub OAuth with redirectTo:",
            githubRedirectUrl,
            "Incognito mode:",
            isIncognitoMode
          );

          // Add this check for storage access before proceeding
          try {
            // Use the appropriate storage mechanism
            const storage =
              typeof window !== "undefined"
                ? isIncognitoMode
                  ? sessionStorage
                  : localStorage
                : null;

            if (storage) {
              const testKey = "oauth_test";
              storage.setItem(testKey, "1");
              storage.removeItem(testKey);
            }
          } catch (storageError) {
            console.error("Storage not available:", storageError);

            if (isIncognitoMode) {
              console.log(
                "Storage issues in incognito mode - proceeding anyway with potential limitations"
              );
            } else {
              set({
                error:
                  "Browser storage is restricted. Try disabling private/incognito mode or adjusting your privacy settings.",
              });
              return {
                success: false,
                error: { message: "Storage access restricted" },
              };
            }
          }

          // Set a flag to indicate OAuth flow is starting
          if (typeof window !== "undefined") {
            try {
              localStorage.setItem(
                "oauth_flow_starting",
                Date.now().toString()
              );
              localStorage.setItem("oauth_provider", "github");
            } catch (e) {
              console.warn("Could not set OAuth flow markers:", e);
            }
          }

          const { data, error } = await supabase.auth.signInWithOAuth({
            provider: "github",
            options: {
              redirectTo: githubRedirectUrl,
              skipBrowserRedirect: false,
              queryParams: {
                // Force consent screen to ensure we get a fresh token
                prompt: "consent",
                access_type: "offline",
              },
            },
          });

          if (error) {
            console.error("Error initiating GitHub login:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          return { success: true };
        } catch (error) {
          console.error("Error logging in with GitHub:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Improved Google login implementation
      loginWithGoogle: async () => {
        set({ loading: true, error: null });

        try {
          // Clear any existing OAuth state
          await clearOAuthState();

          // Small delay to ensure cleanup is complete
          await new Promise((resolve) => setTimeout(resolve, 200));

          // Check for incognito mode
          const isIncognitoMode = await detectIncognitoMode();
          set({ isIncognitoMode });

          // Get the correct redirect URL
          const googleRedirectUrl = getRedirectUrl();

          // Add this check for storage access before proceeding
          try {
            // Use the appropriate storage mechanism
            const storage =
              typeof window !== "undefined"
                ? isIncognitoMode
                  ? sessionStorage
                  : localStorage
                : null;

            if (storage) {
              const testKey = "oauth_test";
              storage.setItem(testKey, "1");
              storage.removeItem(testKey);
            }
          } catch (storageError) {
            console.error("Storage not available:", storageError);

            if (isIncognitoMode) {
              console.log(
                "Storage issues in incognito mode - proceeding anyway with potential limitations"
              );
            } else {
              set({
                error:
                  "Browser storage is restricted. Try disabling private/incognito mode or adjusting your privacy settings.",
              });
              return {
                success: false,
                error: { message: "Storage access restricted" },
              };
            }
          }

          // Set a flag to indicate OAuth flow is starting
          if (typeof window !== "undefined") {
            try {
              localStorage.setItem(
                "oauth_flow_starting",
                Date.now().toString()
              );
              localStorage.setItem("oauth_provider", "google");
            } catch (e) {
              console.warn("Could not set OAuth flow markers:", e);
            }
          }

          const { data, error } = await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
              redirectTo: googleRedirectUrl,
              skipBrowserRedirect: false,
              queryParams: {
                // Force consent screen to ensure we get a fresh token
                prompt: "consent",
                access_type: "offline",
                // Add additional OAuth parameters for Google
                include_granted_scopes: "true",
              },
            },
          });

          if (error) {
            console.error("Error initiating Google login:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          return { success: true };
        } catch (error) {
          console.error("Error logging in with Google:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Improved LinkedIn login implementation
      loginWithLinkedIn: async () => {
        set({ loading: true, error: null });

        try {
          // Log OAuth configuration for debugging
          const oauthConfig = logOAuthConfig();

          // Clear any existing OAuth state
          await clearOAuthState();

          // Small delay to ensure cleanup is complete
          await new Promise((resolve) => setTimeout(resolve, 200));

          // Check for incognito mode
          const isIncognitoMode = await detectIncognitoMode();
          set({ isIncognitoMode });

          // Get the correct redirect URL
          const linkedInRedirectUrl = getRedirectUrl();

          console.log(
            "Initiating LinkedIn OAuth with redirectTo:",
            linkedInRedirectUrl,
            "Incognito mode:",
            isIncognitoMode
          );

          // Add this check for storage access before proceeding
          try {
            // Use the appropriate storage mechanism
            const storage =
              typeof window !== "undefined"
                ? isIncognitoMode
                  ? sessionStorage
                  : localStorage
                : null;

            if (storage) {
              const testKey = "oauth_test";
              storage.setItem(testKey, "1");
              storage.removeItem(testKey);
            }
          } catch (storageError) {
            console.error("Storage not available:", storageError);

            if (isIncognitoMode) {
              console.log(
                "Storage issues in incognito mode - proceeding anyway with potential limitations"
              );
            } else {
              set({
                error:
                  "Browser storage is restricted. Try disabling private/incognito mode or adjusting your privacy settings.",
              });
              return {
                success: false,
                error: { message: "Storage access restricted" },
              };
            }
          }

          // Set a flag to indicate OAuth flow is starting
          if (typeof window !== "undefined") {
            try {
              localStorage.setItem(
                "oauth_flow_starting",
                Date.now().toString()
              );
              localStorage.setItem("oauth_provider", "linkedin");
            } catch (e) {
              console.warn("Could not set OAuth flow markers:", e);
            }
          }

          const { data, error } = await supabase.auth.signInWithOAuth({
            provider: "linkedin_oidc",
            options: {
              redirectTo: linkedInRedirectUrl,
              skipBrowserRedirect: false,
              scopes: "openid profile email",
              queryParams: {
                // Force consent screen to ensure we get a fresh token
                prompt: "consent",
                access_type: "offline",
              },
            },
          });

          if (error) {
            console.error("Error initiating LinkedIn login:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          return { success: true };
        } catch (error) {
          console.error("Error logging in with LinkedIn:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Reset password
      resetPassword: async (email) => {
        set({ loading: true, error: null });

        try {
          // Get the correct redirect URL for password reset
          const protocol = window.location.protocol;
          const baseDomain = getBaseDomain();
          const resetPasswordRedirectUrl = `${protocol}//${baseDomain}/auth/reset-password`;

          console.log("Sending password reset email with redirect:", {
            email,
            redirectTo: resetPasswordRedirectUrl,
            origin,
          });

          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: resetPasswordRedirectUrl,
          });

          if (error) {
            console.error("Error resetting password:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          console.log("Password reset email sent successfully");
          return { success: true };
        } catch (error) {
          console.error("Error resetting password:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Update password (for use after password reset)
      updatePassword: async (newPassword) => {
        set({ loading: true, error: null });

        try {
          console.log("Updating user password");

          const { data, error } = await supabase.auth.updateUser({
            password: newPassword,
          });

          if (error) {
            console.error("Error updating password:", error);
            set({ error: error.message });
            return { success: false, error };
          }

          console.log("Password updated successfully:", data);
          return { success: true, data };
        } catch (error) {
          console.error("Error updating password:", error);
          set({ error: error.message });
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Complete onboarding
      completeOnboarding: async (profileData) => {
        set({ loading: true });

        try {
          // Extract valid fields from profileData
          const { full_name, avatar_url, ...otherData } = profileData;

          // Create valid update object with onboarding_completed always set to true
          const updateData = {
            onboarding_completed: true,
            updated_at: new Date().toISOString(),
          };

          // Add valid fields if they exist
          if (full_name) updateData.full_name = full_name;
          if (avatar_url) updateData.avatar_url = avatar_url;

          // If there are other settings to update, merge them with existing settings
          if (Object.keys(otherData).length > 0) {
            const { data: existingProfile } = await supabase
              .from("profiles")
              .select("settings")
              .eq("id", get().user.id)
              .single();

            const currentSettings = existingProfile?.settings || {};

            updateData.settings = {
              ...currentSettings,
              profile: {
                ...(currentSettings.profile || {}),
                ...otherData,
              },
            };
          }

          console.log(
            "AuthStore: Updating profile with onboarding_completed:",
            updateData.onboarding_completed
          );

          // Update profile in database with explicit update
          const { data: updatedProfile, error } = await supabase
            .from("profiles")
            .update(updateData)
            .eq("id", get().user.id)
            .select()
            .single();

          if (error) throw error;

          // Verify the update was successful
          if (updatedProfile && !updatedProfile.onboarding_completed) {
            console.warn(
              "Warning: onboarding_completed field not set properly, trying direct update"
            );

            // Try a direct update as a fallback
            const { error: directError } = await supabase
              .from("profiles")
              .update({
                onboarding_completed: true,
                updated_at: new Date().toISOString(),
              })
              .eq("id", get().user.id);

            if (directError) {
              console.error(
                "Error in direct update of onboarding_completed:",
                directError
              );
            } else {
              console.log("Direct update of onboarding_completed successful");

              // Update the profile in state with the corrected value
              updatedProfile.onboarding_completed = true;
            }
          }

          // Update local state
          set((state) => ({
            onboardingComplete: !!updatedProfile?.onboarding_completed,
            profile: updatedProfile || state.profile,
          }));

          return { success: true, profile: updatedProfile };
        } catch (error) {
          console.error("Error completing onboarding:", error);
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      // Logout
      logout: async () => {
        set({ loading: true });

        try {
          // Set flag to indicate this is an intentional logout
          localStorage.setItem("intentional_logout", "true");

          // Sign out from Supabase
          const { error } = await supabase.auth.signOut();

          if (error) throw error;

          // Clear cached initialization results first
          set({ cachedInitResult: null });

          // Reset all state to initial values - explicitly include profileData
          set({
            user: null,
            session: null,
            onboardingComplete: false,
            profile: null,
            profileData: {}, // Explicitly reset profileData
            userId: null,
            workspaces: [],
            currentWorkspace: null,
            currentWorkspaceId: null,
            currentWorkspaceSettings: {},
            userSettings: {},
            error: null,
            filePathRoot: "",
            storedAvatars: [],
            selectedPreviewUrl: null,
            isProfileComplete: false,
            initialized: false,
            assistantsInitialized: false,
            isLoading: false,
            cachedInitResult: null, // Explicitly clear cached results
          });

          // Clear all Zustand stores from localStorage
          clearAllStores(get, set);

          try {
            console.log("Clearing all storage...");

            // Clear specific auth session flags first
            const sessionKeys = [
              "auth_callback_processed",
              "auth_callback_timestamp",
              "auth_callback_completed",
              "auth_init_cache",
              "auth_init_timestamp",
              "auth_init_error",
            ];

            sessionKeys.forEach((key) => {
              try {
                sessionStorage.removeItem(key);
              } catch (e) {
                console.warn(`Failed to remove session key: ${key}`, e);
              }
            });

            // Clear specific localStorage items first
            const localStorageKeys = [
              STORAGE_KEY,
              "auth-storage",
              "assistant-storage",
              "thread-storage",
              "tool-storage",
              "notification-storage",
              "oauth_flow_starting",
              "oauth_provider",
              "oauth_exchange_attempts",
              "oauth_last_exchange_time",
              "oauth_rate_limited",
              "oauth_rate_limit_time",
              "supabase.auth.token",
              "oauth_state",
              "sb-localhost-auth-token",
            ];

            localStorageKeys.forEach((key) => {
              try {
                localStorage.removeItem(key);
              } catch (e) {
                console.warn(`Failed to remove localStorage key: ${key}`, e);
              }
            });

            // Now clear everything as a fallback
            try {
              localStorage.clear();
              console.log("All localStorage cleared");
            } catch (e) {
              console.error("Failed to clear localStorage:", e);
            }

            try {
              sessionStorage.clear();
              console.log("All sessionStorage cleared");
            } catch (e) {
              console.error("Failed to clear sessionStorage:", e);
            }

            // Clear any cookies related to authentication
            if (typeof document !== "undefined") {
              document.cookie.split(";").forEach((cookie) => {
                const [name] = cookie.trim().split("=");
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
              });
              console.log("Cleared related cookies");
            }
          } catch (storageError) {
            console.error(
              "Error clearing storage during logout:",
              storageError
            );
            // Continue despite error - we still want to complete the logout process
          }

          // Force a hard redirect to get fresh assets
          // This avoids stale chunk issues after deployments
          window.location.href = window.location.origin;
          return { success: true };
        } catch (error) {
          console.error("Error logging out:", error);
          return { success: false, error };
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Handle session expiration - called when 401 is detected
       * Clears session and redirects to login with message
       */
      handleSessionExpired: async () => {
        // Prevent redirect loops if already on auth pages
        if (window.location.pathname.startsWith('/auth')) {
          return;
        }

        // Prevent multiple redirects
        if (get().isHandlingSessionExpiry) {
          return;
        }
        set({ isHandlingSessionExpiry: true });

        console.warn('[Auth] Session expired, redirecting to login');

        try {
          // Clear session without full logout flow to avoid API calls
          await supabase.auth.signOut({ scope: 'local' });
        } catch (e) {
          // Ignore errors during cleanup
        }

        // Clear local storage
        localStorage.removeItem('app-session');

        // Reset auth state
        set({
          user: null,
          session: null,
          profile: null,
          initialized: true,
          isHandlingSessionExpiry: false,
        });

        // Redirect to login with reason
        window.location.href = '/auth/login?reason=session_expired';
      },

      /* ----------------------- */
      /* --- User Management --- */
      /* ----------------------- */
      updateUserSettings: async (newSettings) => {
        console.log("updateUserSettings", newSettings);
        const currentSettings = get().profile?.settings || {};
        try {
          set({ isLoading: true, error: null });

          const { data, error } = await supabase
            .from("profiles")
            .update({
              settings: {
                ...currentSettings,
                ...newSettings,
              },
              updated_at: new Date().toISOString(),
            })
            .eq("id", get().user.id)
            .select()
            .single();

          if (error) throw error;

          set((state) => ({
            profile: {
              ...state.profile,
              settings: {
                ...currentSettings,
                ...newSettings,
              },
            },
          }));

          return data;
        } catch (error) {
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateUserAvatar: async (file) => {
        try {
          set({ isLoading: true, error: null });

          // Validate file
          if (!file || !file.type.startsWith("image/")) {
            throw new Error("Invalid file type. Please upload an image.");
          }

          if (file.size > 5 * 1024 * 1024) {
            // 5MB limit
            throw new Error("File size too large. Maximum size is 5MB.");
          }

          // Convert file to base64
          const base64Data = await fileToBase64(file);

          // Delete existing avatar if any
          await supabase
            .from("user_images")
            .delete()
            .eq("user_id", get().user.id)
            .eq("image_type", "avatar");

          // Insert new avatar
          const { data: imageRecord, error: uploadError } = await supabase
            .from("user_images")
            .insert({
              user_id: get().user.id,
              image_data: base64Data,
              mime_type: file.type,
              file_name: file.name,
              file_size: file.size,
              image_type: "avatar",
            })
            .select("id")
            .single();

          if (uploadError) throw uploadError;

          // Create a data URL for immediate use
          const avatarUrl = `data:${file.type};base64,${base64Data}`;

          // Get current settings
          const { data: existingProfile, error: fetchError } = await supabase
            .from("profiles")
            .select("settings")
            .eq("id", get().user.id)
            .single();

          if (fetchError) throw fetchError;

          const currentSettings = existingProfile?.settings || {};

          // Update settings with new avatar
          const updatedSettings = {
            ...currentSettings,
            profile: {
              ...(currentSettings.profile || {}),
              avatar_url: avatarUrl,
            },
          };

          // Update profile
          const { error: profileError } = await supabase
            .from("profiles")
            .update({
              avatar_url: avatarUrl,
              settings: updatedSettings,
              updated_at: new Date().toISOString(),
            })
            .eq("id", get().user.id);

          if (profileError) throw profileError;

          // Update local state
          set({
            profile: {
              ...get().profile,
              avatar_url: avatarUrl,
              settings: updatedSettings,
            },
          });

          return avatarUrl;
        } catch (error) {
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateApiKeyMap: async (apiKeyMap) => {
        try {
          set({ isLoading: true, error: null });

          // Get current settings
          const currentProfile = get().profile;
          const currentSettings = currentProfile?.settings || {};

          // Create updated settings with the new API key map
          const updatedSettings = {
            ...currentSettings,
            personalization: {
              ...(currentSettings.personalization || {}),
              advanced: {
                ...(currentSettings.personalization?.advanced || {}),
                api_key_map: apiKeyMap,
              },
            },
          };

          // Update the settings in the database
          const { data, error } = await supabase
            .from("profiles")
            .update({ settings: updatedSettings })
            .eq("id", get().user.id)
            .select()
            .single();

          if (error) throw error;

          // Update local state
          get().setProfile(data);
          return data;
        } catch (error) {
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchUserAvatar: async (userId) => {
        try {
          const { data, error } = await supabase
            .from("user_images")
            .select("image_data, mime_type")
            .eq("user_id", userId)
            .eq("image_type", "avatar")
            .single();

          if (error) throw error;
          if (!data) return null;

          return `data:${data.mime_type};base64,${data.image_data}`;
        } catch (error) {
          console.error("Error fetching avatar:", error);
          return null;
        }
      },
      loadUserSettings: async (userId) => {
        try {
          set({ isLoading: true, error: null });
          const { data: profile, error } = await supabase
            .from("profiles")
            .select("settings")
            .eq("id", userId)
            .single();

          if (error) throw error;

          if (profile?.settings) {
            set({ userSettings: profile.settings });
          }
        } catch (error) {
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchProfile: async (userId) => {
        try {
          set({ isLoading: true, error: null });
          const profile = await SupabaseProfileUtils.getProfileById(userId);
          get().setProfile(profile);
          set({ isLoading: false });
          return profile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      /* ------------------------- */
      /* --- Profile Management --- */
      /* ------------------------- */
      initializeProfileData: async () => {
        try {
          set({ isLoading: true, error: null });

          // 1. Fetch stored avatars
          await get().fetchStoredAvatars();

          // 2. Get and update preview URLs for avatars
          await get().getStoredAvatarUrls();

          set({ isLoading: false });
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      getStoredAvatarUrls: async () => {
        try {
          const avatars = get().storedAvatars;
          if (!avatars || avatars.length === 0) return [];

          const avatarsWithUrls = avatars.map((avatar) => {
            const {
              data: { publicUrl },
            } = supabase.storage
              .from("avatars")
              .getPublicUrl(`images/${avatar.name}`);
            return {
              ...avatar,
              url: publicUrl,
            };
          });

          // Update the stored avatars with URLs
          set({ storedAvatars: avatarsWithUrls });
          return avatarsWithUrls;
        } catch (error) {
          console.error("Error getting avatar URLs:", error);
          set({ error: "Failed to get avatar preview URLs" });
          return [];
        }
      },
      fetchStoredAvatars: async () => {
        const bucketName = "avatars";
        console.log("fetchStoredAvatars", bucketName);
        try {
          const { data, error } = await supabase.storage
            .from(bucketName)
            .list("images/", {
              sortBy: { column: "created_at", order: "desc" },
            });

          if (error) throw error;

          const imageFiles = data.filter((file) =>
            VALID_IMAGE_EXTENSIONS.some((ext) =>
              file.name.toLowerCase().endsWith(`.${ext}`)
            )
          );
          console.log("imageFiles", imageFiles);

          set({ storedAvatars: imageFiles });
          return imageFiles;
        } catch (err) {
          console.error("Error fetching avatars:", err);
          set({ error: "Failed to fetch stored avatars", isLoading: false });
        }
      },
      handlePreviewImage: async (imagePath) => {
        try {
          const {
            data: { publicUrl },
          } = supabase.storage
            .from("avatars")
            .getPublicUrl(`images/${imagePath}`);

          set({ selectedPreviewUrl: publicUrl });
          return publicUrl;
        } catch (err) {
          console.error("Error generating preview URL:", err);
          set({ error: "Failed to generate image preview" });
        }
      },
      updateProfile: async (userId, updates) => {
        try {
          const updatedProfile = await SupabaseProfileUtils.updateProfile(
            userId,
            updates
          );
          set((state) => ({
            ...state,
            profile: {
              ...state.profile,
              ...updatedProfile,
            },
          }));
          return updatedProfile;
        } catch (error) {
          console.error("Error updating profile:", error);
          set({ error: error.message });
          throw error;
        }
      },
      updatePreferences: async (userId, preferences) => {
        try {
          set({ isLoading: true, error: null });
          const updatedProfile = await SupabaseProfileUtils.updatePreferences(
            userId,
            preferences
          );
          get().setProfile(updatedProfile);
          set({ isLoading: false });
          return updatedProfile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },
      updateSettings: async (userId, settingsUpdate) => {
        try {
          if (!userId) throw new Error("User ID is required");

          // More robust validation for settings data
          if (!settingsUpdate) {
            throw new Error("Settings update data is required");
          }

          if (typeof settingsUpdate !== "object") {
            throw new Error("Settings update must be an object");
          }

          if (Object.keys(settingsUpdate).length === 0) {
            console.warn("Empty settings object provided, skipping update");
            return get().profile; // Return current profile without updating
          }

          console.log(
            "authStore: Updating settings for user",
            userId,
            settingsUpdate
          );

          // Update settings in Supabase
          const updatedProfile = await SupabaseProfileUtils.updateSettings(
            userId,
            settingsUpdate
          );

          if (!updatedProfile) {
            throw new Error("Failed to update profile settings in Supabase");
          }

          console.log(
            "authStore: Profile updated successfully",
            updatedProfile
          );

          // Update local state using setProfile to ensure all profile-related state is updated
          get().setProfile(updatedProfile);

          console.log("authStore: Local state updated with new profile");

          return updatedProfile;
        } catch (error) {
          console.error("Error updating settings:", error);
          set({ error: error.message });
          throw error;
        }
      },
      updateSetting: async (userId, key, value) => {
        try {
          set({ isLoading: true, error: null });
          const currentProfile = get().profile;
          const currentSettings = currentProfile?.settings || {};

          const updatedProfile = await SupabaseProfileUtils.updateSettings(
            userId,
            {
              ...currentSettings,
              [key]: value,
            }
          );

          get().setProfile(updatedProfile);
          set({ isLoading: false });
          return updatedProfile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },
      updateTheme: async (userId, theme) => {
        try {
          set({ isLoading: true, error: null });
          const updatedProfile =
            await SupabaseProfileUtils.updateThemePreference(userId, theme);
          get().setProfile(updatedProfile);
          set({ isLoading: false });
          return updatedProfile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },
      updateLanguage: async (userId, language) => {
        try {
          set({ isLoading: true, error: null });
          const updatedProfile =
            await SupabaseProfileUtils.updateLanguagePreference(
              userId,
              language
            );
          get().setProfile(updatedProfile);
          set({ isLoading: false });
          return updatedProfile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },
      updateAvatar: async (userId, avatarUrl) => {
        try {
          set({ isLoading: true, error: null });
          const updatedProfile = await SupabaseProfileUtils.updateAvatar(
            userId,
            avatarUrl
          );
          get().setProfile(updatedProfile);
          set({ isLoading: false });
          return updatedProfile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },
      updateOnboardingStatus: async (userId, hasOnboarded) => {
        try {
          set({ isLoading: true, error: null });
          const updatedProfile =
            await SupabaseProfileUtils.updateOnboardingStatus(
              userId,
              hasOnboarded
            );
          get().setProfile(updatedProfile);
          set({ isLoading: false });
          return updatedProfile;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      /* ---------------------------- */
      /* --- Workspace Management --- */
      /* ---------------------------- */

      // Default permission sets for different roles
      getDefaultPermissions: (role) => {
        const permissions = {
          owner: {
            workspace: {
              edit: true,
              delete: true,
              invite_members: true,
              manage_roles: true,
            },
            files: {
              upload: true,
              delete: true,
              share: true,
            },
            memories: {
              create: true,
              edit: true,
              delete: true,
            },
            settings: {
              view: true,
              edit: true,
            },
          },
          member: {
            workspace: {
              edit: false,
              delete: false,
              invite_members: true,
              manage_roles: false,
            },
            files: {
              upload: true,
              delete: false,
              share: true,
            },
            memories: {
              create: true,
              edit: true,
              delete: false,
            },
            settings: {
              view: true,
              edit: false,
            },
          },
        };
        return permissions[role] || permissions.member;
      },

      // Get workspace members with their roles and permissions
      getWorkspaceMembers: async (workspaceId) => {
        try {
          // First get the workspace members
          const { data: members, error: membersError } = await supabase
            .from("workspace_members")
            .select("*")
            .eq("workspace_id", workspaceId)
            .order("created_at", { ascending: true });

          if (membersError) throw membersError;

          if (!members || members.length === 0) {
            return [];
          }

          // Get the user IDs to fetch profiles
          const userIds = members.map((member) => member.user_id);

          // Fetch profiles for these users
          const { data: profiles, error: profilesError } = await supabase
            .from("profiles")
            .select("id, full_name, email, avatar_url, settings")
            .in("id", userIds);

          if (profilesError) throw profilesError;

          // Combine members with their profiles
          const membersWithProfiles = members.map((member) => {
            const profile = profiles?.find((p) => p.id === member.user_id);
            return {
              ...member,
              profiles: profile || null,
            };
          });

          return membersWithProfiles;
        } catch (error) {
          console.error("Error fetching workspace members:", error);
          throw error;
        }
      },

      // Add a member to workspace with role and permissions
      addWorkspaceMember: async (
        workspaceId,
        userId,
        role = "member",
        customPermissions = null
      ) => {
        try {
          const permissions =
            customPermissions || get().getDefaultPermissions(role);

          const { data, error } = await supabase
            .from("workspace_members")
            .insert([
              {
                workspace_id: workspaceId,
                user_id: userId,
                role,
                permissions,
              },
            ])
            .select("*")
            .single();

          if (error) throw error;
          return data;
        } catch (error) {
          console.error("Error adding workspace member:", error);
          throw error;
        }
      },

      // Update member role and permissions
      updateMemberRole: async (
        workspaceId,
        userId,
        role,
        customPermissions = null
      ) => {
        try {
          const permissions =
            customPermissions || get().getDefaultPermissions(role);

          const { data, error } = await supabase
            .from("workspace_members")
            .update({
              role,
              permissions,
            })
            .eq("workspace_id", workspaceId)
            .eq("user_id", userId)
            .select("*")
            .single();

          if (error) throw error;
          return data;
        } catch (error) {
          console.error("Error updating member role:", error);
          throw error;
        }
      },

      // Remove member from workspace
      removeWorkspaceMember: async (workspaceId, userId) => {
        try {
          const { error } = await supabase
            .from("workspace_members")
            .delete()
            .eq("workspace_id", workspaceId)
            .eq("user_id", userId);

          if (error) throw error;
          return true;
        } catch (error) {
          console.error("Error removing workspace member:", error);
          throw error;
        }
      },

      // Get user's role in a specific workspace
      getUserWorkspaceRole: async (workspaceId, userId) => {
        try {
          const { data, error } = await supabase
            .from("workspace_members")
            .select("role, permissions")
            .eq("workspace_id", workspaceId)
            .eq("user_id", userId)
            .single();

          if (error && error.code !== "PGRST116") throw error;
          return data;
        } catch (error) {
          console.error("Error getting user workspace role:", error);
          return null;
        }
      },

      // Check if user has specific permission in workspace
      hasPermission: async (workspaceId, userId, permissionPath) => {
        try {
          const roleData = await get().getUserWorkspaceRole(
            workspaceId,
            userId
          );
          if (!roleData) return false;

          const permissions = roleData.permissions || {};
          const pathParts = permissionPath.split(".");

          let current = permissions;
          for (const part of pathParts) {
            if (current[part] === undefined) return false;
            current = current[part];
          }

          return current === true;
        } catch (error) {
          console.error("Error checking permission:", error);
          return false;
        }
      },

      // Check if user is workspace owner
      isWorkspaceOwner: async (workspaceId, userId) => {
        try {
          const roleData = await get().getUserWorkspaceRole(
            workspaceId,
            userId
          );
          return roleData?.role === "owner";
        } catch (error) {
          console.error("Error checking workspace ownership:", error);
          return false;
        }
      },

      createWorkspace: async (workspaceData) => {
        try {
          set({ isLoading: true, error: null });

          const userId = get().user.id;

          // Filter out fields that don't exist in the database schema
          const { logo, members, ...validWorkspaceData } = workspaceData;

          const { data: workspace, error: workspaceError } = await supabase
            .from("workspaces")
            .insert([
              {
                ...validWorkspaceData,
                user_id: userId,
                owner_id: userId,
                // Ensure the creator is added to workspace_members array
                workspace_members: workspaceData.workspace_members || [userId],
              },
            ])
            .select()
            .single();

          if (workspaceError) throw workspaceError;

          // Add creator as owner in workspace_members table
          await get().addWorkspaceMember(workspace.id, userId, "owner");

          set((state) => ({
            workspaces: [...state.workspaces, workspace],
            currentWorkspace: workspace,
          }));

          return workspace;
        } catch (error) {
          set({ error: error.message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      switchWorkspace: (workspaceId) => {
        const workspace = get().workspaces.find((w) => w.id === workspaceId);
        if (workspace) {
          set({ currentWorkspace: workspace });
        }
      },
      updateWorkspace: async (workspaceId, updates) => {
        try {
          const { error } = await supabase
            .from("workspaces")
            .update({
              name: updates.name,
              description: updates.description,
              image_path: updates.image_path,
              include_profile_context: updates.include_profile_context,
              include_workspace_instructions:
                updates.include_workspace_instructions,
              instructions: updates.instructions,
              is_home: updates.is_home,
              workspace_assistants: updates.workspace_assistants,
              updated_at: new Date().toISOString(),
            })
            .eq("id", workspaceId)
            .eq("user_id", get().user.id); // Ensure user can only update their own workspaces

          if (error) throw error;

          // Update local state
          set((state) => ({
            workspaces: state.workspaces.map((w) =>
              w.id === workspaceId ? { ...w, ...updates } : w
            ),
            currentWorkspace: { ...state.currentWorkspace, ...updates },
          }));

          return true;
        } catch (error) {
          console.error("Error updating workspace:", error);
          throw error;
        }
      },
      updateWorkspaceField: async (workspaceId, dataType, newData) => {
        // set({ isLoading: true });
        try {
          const { data, error } = await supabase
            .from("workspaces")
            .update({ [`${dataType}`]: newData })
            .eq("id", workspaceId)
            .select();

          if (error) throw error;
          set((state) => ({
            workspaces: state.workspaces.map((w) =>
              w.id === workspaceId ? { ...w, [`${dataType}`]: newData } : w
            ),
            currentWorkspace:
              state.currentWorkspace?.id === workspaceId
                ? { ...state.currentWorkspace, [`${dataType}`]: newData }
                : state.currentWorkspace,
          }));
        } catch (error) {
          set({ error: error.message });
        }
      },
      setCurrentWorkspace: (workspace) => {
        set({ currentWorkspace: workspace });
      },

      /* ---------------------------- */
      /* ---- Workspace Updates ----- */
      /* ---------------------------- */

      /**
       * Updates the workspace_memories array in the current workspace
       * @param {string} workspaceId - The ID of the workspace
       * @param {string} memoryId - The ID of the memory to add or remove
       * @param {string} action - Either 'add' or 'remove'
       * @returns {boolean} - Success status
       */
      updateWorkspaceMemories: (workspaceId, memoryId, action = "add") => {
        try {
          // Validate inputs
          if (!workspaceId || !memoryId) {
            console.error(
              "Missing workspaceId or memoryId in updateWorkspaceMemories"
            );
            return false;
          }

          // Get current state
          const currentState = get();
          const currentWorkspace = currentState.currentWorkspace;

          // Ensure we have the current workspace
          if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
            console.error(
              "Current workspace not found or does not match workspaceId"
            );
            return false;
          }

          // Get current memories array
          const currentMemories = currentWorkspace.workspace_memories || [];

          // Update memories array based on action
          let updatedMemories;
          if (action === "add") {
            // Add memory ID if it doesn't exist already
            updatedMemories = [...new Set([...currentMemories, memoryId])];
          } else if (action === "remove") {
            // Remove memory ID
            updatedMemories = currentMemories.filter((id) => id !== memoryId);
          } else {
            console.error("Invalid action in updateWorkspaceMemories:", action);
            return false;
          }

          // Create updated workspace with new memories array
          const updatedWorkspace = {
            ...currentWorkspace,
            workspace_memories: updatedMemories,
          };

          // Update workspaces array to maintain consistency
          const updatedWorkspaces = currentState.workspaces.map((workspace) =>
            workspace.id === workspaceId ? updatedWorkspace : workspace
          );

          // Update state atomically to prevent UI inconsistencies
          set({
            currentWorkspace: updatedWorkspace,
            workspaces: updatedWorkspaces,
          });

          return true;
        } catch (error) {
          console.error(
            "Error updating workspace memories in authStore:",
            error
          );
          return false;
        }
      },

      /**
       * Updates the workspace_files array in the current workspace
       * @param {string} workspaceId - The ID of the workspace
       * @param {string} fileId - The ID of the file to add or remove
       * @param {string} action - Either 'add' or 'remove'
       * @returns {boolean} - Success status
       */
      updateWorkspaceFiles: (workspaceId, fileId, action = "add") => {
        try {
          // Validate inputs
          if (!workspaceId || !fileId) {
            console.error(
              "Missing workspaceId or fileId in updateWorkspaceFiles"
            );
            return false;
          }

          // Get current state
          const currentState = get();
          const currentWorkspace = currentState.currentWorkspace;

          // Ensure we have the current workspace
          if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
            console.error(
              "Current workspace not found or does not match workspaceId"
            );
            return false;
          }

          // Get current files array
          const currentFiles = currentWorkspace.workspace_files || [];

          // Update files array based on action
          let updatedFiles;
          if (action === "add") {
            // Add file ID if it doesn't exist already
            updatedFiles = [...new Set([...currentFiles, fileId])];
          } else if (action === "remove") {
            // Remove file ID
            updatedFiles = currentFiles.filter((id) => id !== fileId);
          } else {
            console.error("Invalid action in updateWorkspaceFiles:", action);
            return false;
          }

          // Create updated workspace with new files array
          const updatedWorkspace = {
            ...currentWorkspace,
            workspace_files: updatedFiles,
          };

          // Update workspaces array to maintain consistency
          const updatedWorkspaces = currentState.workspaces.map((workspace) =>
            workspace.id === workspaceId ? updatedWorkspace : workspace
          );

          // Update state atomically to prevent UI inconsistencies
          set({
            currentWorkspace: updatedWorkspace,
            workspaces: updatedWorkspaces,
          });

          return true;
        } catch (error) {
          console.error("Error updating workspace files in authStore:", error);
          return false;
        }
      },

      /**
       * Updates the workspace_tasks array in the current workspace
       * @param {string} workspaceId - The ID of the workspace
       * @param {string} taskId - The ID of the task to add or remove
       * @param {string} action - Either 'add' or 'remove'
       * @returns {boolean} - Success status
       */
      updateWorkspaceTasks: (workspaceId, taskId, action = "add") => {
        try {
          // Validate inputs
          if (!workspaceId || !taskId) {
            console.error(
              "Missing workspaceId or taskId in updateWorkspaceTasks"
            );
            return false;
          }

          // Get current state
          const currentState = get();
          const currentWorkspace = currentState.currentWorkspace;

          // Ensure we have the current workspace
          if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
            console.error(
              "Current workspace not found or does not match workspaceId"
            );
            return false;
          }

          // Get current tasks array
          const currentTasks = currentWorkspace.workspace_tasks || [];

          // Update tasks array based on action
          let updatedTasks;
          if (action === "add") {
            // Add task ID if it doesn't exist already
            updatedTasks = [...new Set([...currentTasks, taskId])];
          } else if (action === "remove") {
            // Remove task ID
            updatedTasks = currentTasks.filter((id) => id !== taskId);
          } else {
            console.error("Invalid action in updateWorkspaceTasks:", action);
            return false;
          }

          // Create updated workspace with new tasks array
          const updatedWorkspace = {
            ...currentWorkspace,
            workspace_tasks: updatedTasks,
          };

          // Update workspaces array to maintain consistency
          const updatedWorkspaces = currentState.workspaces.map((workspace) =>
            workspace.id === workspaceId ? updatedWorkspace : workspace
          );

          // Update state atomically to prevent UI inconsistencies
          set({
            currentWorkspace: updatedWorkspace,
            workspaces: updatedWorkspaces,
          });

          return true;
        } catch (error) {
          console.error("Error updating workspace tasks in authStore:", error);
          return false;
        }
      },

      /**
       * Updates the workspace_workflows array in the current workspace
       * @param {string} workspaceId - The ID of the workspace
       * @param {string} workflowId - The ID of the workflow to add or remove
       * @param {string} action - Either 'add' or 'remove'
       * @returns {boolean} - Success status
       */
      updateWorkspaceWorkflows: (workspaceId, workflowId, action = "add") => {
        try {
          // Validate inputs
          if (!workspaceId || !workflowId) {
            console.error(
              "Missing workspaceId or workflowId in updateWorkspaceWorkflows"
            );
            return false;
          }

          // Get current state
          const currentState = get();
          const currentWorkspace = currentState.currentWorkspace;

          // Ensure we have the current workspace
          if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
            console.error(
              "Current workspace not found or does not match workspaceId"
            );
            return false;
          }

          // Get current workflows array
          const currentWorkflows = currentWorkspace.workspace_workflows || [];

          // Update workflows array based on action
          let updatedWorkflows;
          if (action === "add") {
            // Add workflow ID if it doesn't exist already
            updatedWorkflows = [...new Set([...currentWorkflows, workflowId])];
          } else if (action === "remove") {
            // Remove workflow ID
            updatedWorkflows = currentWorkflows.filter(
              (id) => id !== workflowId
            );
          } else {
            console.error(
              "Invalid action in updateWorkspaceWorkflows:",
              action
            );
            return false;
          }

          // Create updated workspace with new workflows array
          const updatedWorkspace = {
            ...currentWorkspace,
            workspace_workflows: updatedWorkflows,
          };

          // Update workspaces array to maintain consistency
          const updatedWorkspaces = currentState.workspaces.map((workspace) =>
            workspace.id === workspaceId ? updatedWorkspace : workspace
          );

          // Update state atomically to prevent UI inconsistencies
          set({
            currentWorkspace: updatedWorkspace,
            workspaces: updatedWorkspaces,
          });

          return true;
        } catch (error) {
          console.error(
            "Error updating workspace workflows in authStore:",
            error
          );
          return false;
        }
      },

      /* ---------------------------- */
      /* --------- Selectors -------- */
      /* ---------------------------- */
      getProfileData: () => get().profile,
      getLoadingState: () => get().isLoading,
      getError: () => get().error,
      getThemePreference: () =>
        get().profile?.settings?.system?.theme || "light",
      getLanguagePreference: () =>
        get().profile?.settings?.system?.language || "en",
      getHasOnboarded: () => get().profile?.onboarding_completed || false,
      getPreferences: () => get().profile?.settings?.profile || {},
      getDisplayName: () =>
        get().profile?.settings?.profile?.display_name || "",
      getFullName: () => get().profile?.full_name || "",
      getAvatarUrl: () => get().profile?.avatar_url || "",
      getWorkspaces: () => get().workspaces || [],
      getCurrentWorkspace: () => get().currentWorkspace || null,
      getCurrentWorkspaceId: () => get().currentWorkspaceId || null,
      getFilePathRoot: () => get().filePathRoot || "",
      getSelectedPreviewUrl: () => get().selectedPreviewUrl || null,
      getStoredAvatars: () => get().storedAvatars || [],
      getProfileSettings: () => get().profile?.settings || {},
      getProfile: () => get().profile || {},
      getProfileEnvKeyMap: () =>
        get().profile?.settings?.personalization?.advanced?.api_key_map || {},
      getKeyFromProfileEnvKeyMap: (key) =>
        get().profile?.settings?.personalization?.advanced?.api_key_map?.[
          key
        ] || "",
      initializeAssistantStore: async () => {
        // First ensure we have completed basic initialization
        const state = get();
        if (!state.initialized) {
          const initResult = await get().initialize();
          if (!initResult.success) {
            console.error(
              "Failed to initialize auth before assistants:",
              initResult.reason
            );
            return null;
          }
        }

        // Get fresh state after potential initialization
        const {
          userId,
          user,
          currentWorkspaceId,
          currentWorkspace,
          workspaces,
        } = get();

        if (!userId && !user?.id) {
          console.error(
            "Cannot initialize assistant store: No user ID available"
          );
          return null;
        }

        // Verify we have a current workspace ID
        if (!currentWorkspaceId || !currentWorkspace) {
          console.error(
            "Cannot initialize assistant store: Incomplete workspace information",
            { id: currentWorkspaceId, workspace: Boolean(currentWorkspace) }
          );

          // Attempt to recover the workspace ID and object
          if (workspaces && workspaces.length > 0) {
            const workspace = findEarliestWorkspace(workspaces);

            // Set both properties atomically
            set({
              currentWorkspaceId: workspace.id,
              currentWorkspace: workspace,
              filePathRoot: `${userId || user?.id}/${workspace.id}`,
            });
          } else {
            // If no workspaces in state, try to fetch them
            try {
              const { data: fetchedWorkspaces } = await supabase
                .from("workspaces")
                .select("*")
                .eq("user_id", userId || user?.id);

              if (fetchedWorkspaces && fetchedWorkspaces.length > 0) {
                const workspace = findEarliestWorkspace(fetchedWorkspaces);

                // Set all workspace properties atomically
                set({
                  workspaces: fetchedWorkspaces,
                  currentWorkspaceId: workspace.id,
                  currentWorkspace: workspace,
                  filePathRoot: `${userId || user?.id}/${workspace.id}`,
                });
              } else {
                // Create a personal workspace if none exist
                console.log("No workspaces found, creating personal workspace");
                const { data: newWorkspace, error: createError } =
                  await supabase
                    .from("workspaces")
                    .insert([
                      {
                        user_id: userId || user?.id,
                        owner_id: userId || user?.id,
                        name: "Personal Project",
                        is_personal: true,
                        settings: {},
                        workspace_members: [userId || user?.id],
                        metadata: {
                          created_at: new Date().toISOString(),
                          created_by: userId || user?.id,
                        },
                      },
                    ])
                    .select()
                    .single();

                if (createError) {
                  console.error(
                    "Error creating personal workspace:",
                    createError
                  );
                  return null;
                }

                if (newWorkspace) {
                  set({
                    workspaces: [newWorkspace],
                    currentWorkspaceId: newWorkspace.id,
                    currentWorkspace: newWorkspace,
                    filePathRoot: `${userId || user?.id}/${newWorkspace.id}`,
                  });
                }
              }
            } catch (workspaceError) {
              console.error(
                "Error fetching workspaces during recovery:",
                workspaceError
              );
              return null;
            }
          }
        }

        const userIdToUse = userId || user?.id;
        const workspaceIdToUse = get().currentWorkspaceId; // Get the possibly updated value

        // Final check before initializing assistants
        if (!workspaceIdToUse) {
          console.error("Still no workspace ID after recovery attempts");
          return null;
        }

        try {
          // Dynamically import the assistant store to avoid circular dependencies
          const { useAssistantStore } = await import("@/store/assistantStore");
          const assistantStore = useAssistantStore.getState();

          // Make sure we're not in onboarding mode
          assistantStore.setInitializedFromOnboarding(false);

          // Force a fresh initialization
          useAssistantStore.setState({
            initialized: false,
            initializing: false,
          });

          // Initialize the assistant store (it gets userId and workspaceId from auth store internally)
          const assistants = await assistantStore.initializeAssistants();

          // Mark assistants as initialized in auth store
          set({ assistantsInitialized: true });

          return assistants;
        } catch (error) {
          console.error("Error initializing assistant store:", error);
          return null;
        }
      },
      setAssistantsInitialized: (initialized) =>
        set({ assistantsInitialized: initialized }),
      initializeWorkspaces: async (userId) => {
        try {
          // Only fetch if we don't already have workspaces
          if (get().workspaces.length === 0) {
            // Fetch owned workspaces
            const { data: ownedWorkspaces, error: ownedError } = await supabase
              .from("workspaces")
              .select("*")
              .eq("user_id", userId);

            if (ownedError) throw ownedError;

            // Fetch workspace memberships
            const { data: memberships, error: membershipsError } =
              await supabase
                .from("workspace_members")
                .select("workspace_id")
                .eq("user_id", userId);

            if (membershipsError) throw membershipsError;

            // Get the user's profile to check for user_workspaces
            const { data: userProfile, error: userProfileError } =
              await supabase
                .from("profiles")
                .select("user_workspaces")
                .eq("id", userId)
                .single();

            if (userProfileError) {
              console.error("Error loading user profile:", userProfileError);
              // Continue despite error
            }

            // Parse user_workspaces from profile if it exists
            let userWorkspaceIds = [];
            if (userProfile?.user_workspaces) {
              try {
                // Handle both array and string JSON formats
                if (typeof userProfile.user_workspaces === "string") {
                  userWorkspaceIds = JSON.parse(userProfile.user_workspaces);
                } else if (Array.isArray(userProfile.user_workspaces)) {
                  userWorkspaceIds = userProfile.user_workspaces;
                }
              } catch (error) {
                console.error("Error parsing user_workspaces:", error);
              }
            }

            // Combine all workspace IDs from memberships and user_workspaces
            const membershipIds = (memberships || []).map(
              (m) => m.workspace_id
            );
            const allUniqueWorkspaceIds = [
              ...new Set([...membershipIds, ...userWorkspaceIds]),
            ];

            // If we have any combined workspace IDs, fetch those workspaces
            let nonOwnedWorkspaces = [];
            if (allUniqueWorkspaceIds.length > 0) {
              const { data: fetchedWorkspaces, error: fetchError } =
                await supabase
                  .from("workspaces")
                  .select("*")
                  .in("id", allUniqueWorkspaceIds);

              if (fetchError) {
                console.error("Error loading workspaces:", fetchError);
              } else if (fetchedWorkspaces) {
                nonOwnedWorkspaces = fetchedWorkspaces;
              }
            }

            // Combine owned and non-owned workspaces, ensuring no duplicates
            const ownedWorkspaceIds = new Set(
              (ownedWorkspaces || []).map((w) => w.id)
            );
            const uniqueNonOwnedWorkspaces = (nonOwnedWorkspaces || []).filter(
              (w) => !ownedWorkspaceIds.has(w.id)
            );

            const allWorkspaces = [
              ...(ownedWorkspaces || []),
              ...uniqueNonOwnedWorkspaces,
            ];

            if (allWorkspaces && allWorkspaces.length > 0) {
              // Find personal workspace or use first one
              const personalWorkspace = findEarliestWorkspace(allWorkspaces);

              set({
                workspaces: allWorkspaces,
                currentWorkspace: personalWorkspace,
                currentWorkspaceId: personalWorkspace.id,
              });

              return personalWorkspace;
            }
          }
          return get().currentWorkspace;
        } catch (error) {
          console.error("Error initializing workspaces:", error);
          return null;
        }
      },

      /* ---------------------------- */
      /* --------- Getters ---------- */
      /* ---------------------------- */
      getIsPremiumUser: () =>
        get().profile?.settings?.subscription?.plan === "pro" || false,

      /**
       * Builds instruction context from current profile data with XML tags
       * @returns {string} - Formatted instruction context with XML tags
       */
      getProfileInstructionContext: () => {
        const profile = get().profile;
        if (!profile || !profile.settings) {
          return "";
        }

        const settings = profile.settings;
        let instructionContext = "";

        // Extract traits from personalization settings
        const traits =
          settings.personalization?.traits ||
          settings.settings?.personalization?.traits ||
          [];
        if (traits && traits.length > 0) {
          instructionContext += `<traits>\n${traits.join(", ")}\n</traits>\n\n`;
        }

        // Extract role from personalization settings
        const role =
          settings.personalization?.role ||
          settings.settings?.personalization?.role ||
          "";
        if (role) {
          instructionContext += `<role>\n${role}\n</role>\n\n`;
        }

        // Extract profile_context from profile settings
        const profileContext = settings.profile?.profile_context || "";
        if (profileContext) {
          instructionContext += `<profile_context>\n${profileContext}\n</profile_context>\n\n`;
        }

        return instructionContext.trim();
      },

      /**
       * Builds workspace instruction context from current workspace data with XML tags
       * @returns {string} - Formatted workspace instruction context with XML tags
       */
      getWorkspaceInstructionContext: () => {
        const workspace = get().currentWorkspace;
        if (!workspace) {
          return "";
        }

        let workspaceContext = "";

        // Extract workspace name
        const name = workspace.name || "";
        if (name) {
          workspaceContext += `<name>\n${name}\n</name>\n\n`;
        }

        // Extract workspace description
        const description = workspace.description || "";
        if (description) {
          workspaceContext += `<description>\n${description}\n</description>\n\n`;
        }

        // Extract workspace instructions
        const instructions = workspace.instructions || "";
        if (instructions) {
          workspaceContext += `<instructions>\n${instructions}\n</instructions>\n\n`;
        }

        // Wrap everything in user_project tags if there's any content
        if (workspaceContext.trim()) {
          return `<user_project>\n${workspaceContext.trim()}\n</user_project>`;
        }

        return "";
      },

      /**
       * Gets workspace context data for chat instruction building
       * @returns {Object} - Workspace context data object
       */
      getWorkspaceContextData: () => {
        const workspace = get().currentWorkspace;
        if (!workspace) {
          console.log("🔍 No current workspace found in auth store");
          return null;
        }

        console.log("🏗️ Getting workspace context data from auth store", {
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          hasDescription: !!workspace.description,
          hasInstructions: !!workspace.instructions,
          includeWorkspaceInstructions:
            workspace.include_workspace_instructions,
          includeProfileContext: workspace.include_profile_context,
        });

        return {
          id: workspace.id,
          name: workspace.name || "",
          description: workspace.description || "",
          instructions: workspace.instructions || "",
          include_profile_context: workspace.include_profile_context || false,
          include_workspace_instructions:
            workspace.include_workspace_instructions || true,
          vector_store_id: workspace.vector_store_id || null,
          is_team: workspace.is_team || false,
        };
      },

      /**
       * Gets the personal workspace ID from the workspaces array
       * @returns {string|null} - Personal workspace ID or null if not found
       */
      getPersonalWorkspaceId: () => {
        const { workspaces, currentWorkspaceId } = get();

        // First try to find a workspace marked as personal
        const personalWorkspace = workspaces.find(
          (w) => w.is_personal === true
        );
        if (personalWorkspace) {
          return personalWorkspace.id;
        }

        // Fallback to current workspace if no personal workspace found
        if (currentWorkspaceId) {
          return currentWorkspaceId;
        }

        // Last resort: return the first workspace if any exist
        if (workspaces && workspaces.length > 0) {
          return workspaces[0].id;
        }

        return null;
      },

      addWorkspace: (workspace) => {
        if (!workspace) return;

        // Add workspace to workspaces array if not already present
        set((state) => {
          const existingIndex = state.workspaces.findIndex(
            (w) => w.id === workspace.id
          );

          if (existingIndex === -1) {
            // Workspace doesn't exist yet, add it
            return {
              workspaces: [...state.workspaces, workspace],
            };
          }

          // Workspace already exists, update it
          const updatedWorkspaces = [...state.workspaces];
          updatedWorkspaces[existingIndex] = workspace;

          return {
            workspaces: updatedWorkspaces,
          };
        });
      },

      refreshUserProfile: async () => {
        try {
          set({ isLoading: true });
          const userId = get().user?.id;

          if (!userId) {
            console.error("Cannot refresh profile: No user ID available");
            return null;
          }

          console.log("Refreshing user profile and workspaces for:", userId);

          // Get the fresh profile data from the database
          const { data: freshProfile, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .single();

          if (profileError) {
            console.error("Error fetching fresh profile data:", profileError);
            throw profileError;
          }

          // Update the profile in the store using setProfile to ensure profileData is also updated
          get().setProfile(freshProfile);
          console.log("Updated profile with refreshed data:", freshProfile);

          // Parse user_workspaces from the fresh profile
          let userWorkspaceIds = [];
          if (freshProfile.user_workspaces) {
            try {
              // Handle both array and string JSON formats
              if (typeof freshProfile.user_workspaces === "string") {
                userWorkspaceIds = JSON.parse(freshProfile.user_workspaces);
              } else if (Array.isArray(freshProfile.user_workspaces)) {
                userWorkspaceIds = freshProfile.user_workspaces;
              }
              console.log(
                `Refreshed profile has ${userWorkspaceIds.length} workspaces in user_workspaces:`,
                userWorkspaceIds
              );
            } catch (error) {
              console.error("Error parsing user_workspaces:", error);
            }
          }

          // First, get workspaces owned by the user
          const { data: ownedWorkspaces, error: ownedWorkspacesError } =
            await supabase.from("workspaces").select("*").eq("user_id", userId);

          if (ownedWorkspacesError) {
            console.error(
              "Error loading owned workspaces:",
              ownedWorkspacesError
            );
          }

          // Then get workspace IDs where user is a member
          const { data: memberships, error: membershipsError } = await supabase
            .from("workspace_members")
            .select("workspace_id")
            .eq("user_id", userId);

          if (membershipsError) {
            console.error(
              "Error loading workspace memberships:",
              membershipsError
            );
          }

          // Combine all workspace IDs from memberships and user_workspaces
          const membershipIds = (memberships || []).map((m) => m.workspace_id);
          const allUniqueWorkspaceIds = [
            ...new Set([...membershipIds, ...userWorkspaceIds]),
          ];

          console.log(
            `Refresh: Combined unique workspace IDs: ${allUniqueWorkspaceIds.length} total`,
            allUniqueWorkspaceIds
          );

          // Fetch workspaces based on the combined IDs
          let nonOwnedWorkspaces = [];
          if (allUniqueWorkspaceIds.length > 0) {
            console.log(
              `Refresh: Fetching ${allUniqueWorkspaceIds.length} workspaces from combined sources`
            );

            const { data: fetchedWorkspaces, error: fetchError } =
              await supabase
                .from("workspaces")
                .select("*")
                .in("id", allUniqueWorkspaceIds);

            if (fetchError) {
              console.error("Error loading workspaces:", fetchError);
            } else if (fetchedWorkspaces) {
              nonOwnedWorkspaces = fetchedWorkspaces;
              console.log(
                `Refresh: Successfully fetched ${nonOwnedWorkspaces.length} non-owned workspaces`
              );
            }
          }

          // Combine owned and non-owned workspaces, ensuring no duplicates
          const ownedWorkspaceIds = new Set(
            (ownedWorkspaces || []).map((w) => w.id)
          );
          const uniqueNonOwnedWorkspaces = (nonOwnedWorkspaces || []).filter(
            (w) => !ownedWorkspaceIds.has(w.id)
          );

          const allWorkspaces = [
            ...(ownedWorkspaces || []),
            ...uniqueNonOwnedWorkspaces,
          ];

          console.log(
            `Refresh: Total workspaces available to user: ${allWorkspaces.length}`
          );

          // Update the workspaces in the store
          set({ workspaces: allWorkspaces });

          // Update current workspace if needed
          const currentWorkspace = get().currentWorkspace;
          if (!currentWorkspace && allWorkspaces.length > 0) {
            // If we have workspaces but no current one is set, use the personal one or the first available
            const personalWorkspace = findEarliestWorkspace(allWorkspaces);
            set({
              currentWorkspace: personalWorkspace,
              currentWorkspaceId: personalWorkspace.id,
              filePathRoot: `${userId}/${personalWorkspace.id}`,
            });
            console.log(
              "Set current workspace to:",
              personalWorkspace.name,
              personalWorkspace.id
            );
          }

          return { profile: freshProfile, workspaces: allWorkspaces };
        } catch (error) {
          console.error("Error refreshing user profile:", error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      // Add method to check and set incognito mode status
      checkIncognitoMode: async () => {
        const isIncognito = await detectIncognitoMode();
        set({ isIncognitoMode: isIncognito });
        return isIncognito;
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        // Only persist these fields
        initialized: state.initialized,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        userId: state.userId,
        profile: state.profile,
        workspaces: state.workspaces,
        currentWorkspace: state.currentWorkspace,
        currentWorkspaceId: state.currentWorkspaceId,
        isProfileComplete: state.isProfileComplete,
        onboardingComplete: state.onboardingComplete,
        filePathRoot: state.filePathRoot,
        storedAvatars: state.storedAvatars,
        selectedPreviewUrl: state.selectedPreviewUrl,
        error: state.error,
        profileData: state.profileData,
        isIncognitoMode: state.isIncognitoMode,
      }),
      // Optional: Add storage configuration
      storage: {
        getItem: (name) => {
          const storage = getStorage();
          if (!storage) return null;

          const str = storage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch (e) {
            console.error("Error parsing stored state:", e);
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            const storage = getStorage();
            if (storage) {
              storage.setItem(name, JSON.stringify(value));
            }
          } catch (e) {
            console.error("Error storing state:", e);
          }
        },
        removeItem: (name) => {
          const storage = getStorage();
          if (storage) {
            storage.removeItem(name);
          }
        },
      },
    }
  )
);

export default useAuthStore;
